// Connector consent page (#141 W8 follow-up).
//
// The MCP OAuth authorization endpoint lives on the BACKEND, which a browser redirect
// from Claude reaches without our Supabase session (the session is in localStorage on
// the frontend origin, not a cross-domain cookie). So the backend /oauth/authorize, when
// it can't identify the caller, redirects the browser here. This page holds the logged-in
// session and re-calls the backend authorize with ?subject_token=, which then issues the
// code and redirects back to Claude. It only renders for an authenticated, approved user
// (App.js <AuthGate> guarantees that before the router mounts).
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Alert, Box, Button, Chip, FormControlLabel, Checkbox, Paper, Stack, Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { resolveApiBaseUrl } from '../services/resolveApiBaseUrl';
import { iamApi } from '../services/iamApi';

// OAuth params forwarded verbatim back to the backend authorize endpoint. `project` and
// `read_only` are handled separately below (#519 item 3: the user picks these on THIS
// page rather than a bare pass-through — see selectedProjects/readOnly).
const PASS_THROUGH = [
  'response_type', 'client_id', 'redirect_uri', 'code_challenge',
  'code_challenge_method', 'scope', 'state', 'resource',
];

// resolveApiBaseUrl() ends with /api; the OAuth + /mcp endpoints are at the root.
const backendOrigin = () => resolveApiBaseUrl().replace(/\/api\/?$/, '');

export default function ConnectConsent() {
  const [params] = useSearchParams();
  const { token, user, activeOrg } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  // #519 item 3: the consent screen supports scoping to MULTIPLE projects + a read-only
  // choice, mirroring the self-serve "issue a personal token" mint UI (iam/pages.jsx).
  // Pre-seed from the incoming URL (repeated ?project= params, or a comma-separated
  // value) so a caller that already knows which project(s) it wants still works with no
  // extra clicks; empty selection = unscoped (every project the principal may reach),
  // identical to the pre-#519 behaviour.
  const [selectedProjects, setSelectedProjects] = useState(() => {
    const repeated = params.getAll('project');
    if (repeated.length > 1) return repeated;
    const single = params.get('project');
    return single ? single.split(',').map((s) => s.trim()).filter(Boolean) : [];
  });
  const [readOnly, setReadOnly] = useState(
    ['1', 'true'].includes((params.get('read_only') || '').toLowerCase()),
  );
  const [myProjects, setMyProjects] = useState([]);
  useEffect(() => {
    if (!activeOrg) { setMyProjects([]); return; }
    iamApi.myProjects(activeOrg).then(setMyProjects).catch(() => setMyProjects([]));
  }, [activeOrg]);
  const toggleProject = (pid) => {
    setSelectedProjects((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]));
  };

  const redirectUri = params.get('redirect_uri') || '';
  const resource = params.get('resource') || `${backendOrigin()}/mcp`;
  const missing = !params.get('response_type') || !params.get('client_id')
    || !redirectUri || !params.get('code_challenge');

  const approve = () => {
    setSubmitting(true);
    const q = new URLSearchParams();
    PASS_THROUGH.forEach((k) => { const v = params.get(k); if (v) q.set(k, v); });
    // The backend requires an explicit org when the user belongs to more than one;
    // scope the connector to the active org. subject_token proves identity.
    const org = params.get('org') || activeOrg;
    if (org) q.set('org', org);
    // #519 item 3: repeated project= params (empty selection -> omitted -> unscoped,
    // same "every project the principal may reach" behaviour as today) + read_only.
    selectedProjects.forEach((pid) => q.append('project', pid));
    if (readOnly) q.set('read_only', 'true');
    q.set('subject_token', token || '');
    // Full-page navigation so the backend's final 302 lands on Claude's callback.
    window.location.href = `${backendOrigin()}/oauth/authorize?${q.toString()}`;
  };

  const cancel = () => {
    if (redirectUri) {
      const q = new URLSearchParams({ error: 'access_denied' });
      const st = params.get('state');
      if (st) q.set('state', st);
      window.location.href = `${redirectUri}?${q.toString()}`;
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 480, width: '100%' }}>
        <Typography variant="h5" gutterBottom>Connect Claude to RMDV</Typography>
        {missing ? (
          <Alert severity="error">
            This authorization link is missing required parameters. Start the connection again from Claude.
          </Alert>
        ) : !token ? (
          <Alert severity="warning">Please sign in first, then re-open the connection link from Claude.</Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{user?.email || 'You'}</strong> are about to authorise a Claude connector for{' '}
              <code style={{ wordBreak: 'break-all' }}>{resource}</code>. It acts as you, under your existing
              permissions, scoped to your organisation.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Claude will be able to read and (per your roles) modify your requirements via MCP. You can revoke
              this anytime by removing the connector in Claude.
            </Alert>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Scope to project(s) (optional)</Typography>
            {myProjects.length === 0 ? (
              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                No projects yet — the connector will be unscoped (every project you can access).
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
                {myProjects.map((p) => {
                  const on = selectedProjects.includes(p.id);
                  return (
                    <Chip
                      key={p.id}
                      label={p.name || p.title || p.id}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() => toggleProject(p.id)}
                      data-testid={`consent-project-${p.id}`}
                    />
                  );
                })}
              </Stack>
            )}
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              Leave none selected to scope to every project you can access.
            </Typography>
            <FormControlLabel
              sx={{ mb: 2, display: 'block' }}
              control={(
                <Checkbox
                  checked={readOnly}
                  onChange={(e) => setReadOnly(e.target.checked)}
                  data-testid="consent-read-only-checkbox"
                />
              )}
              label="Read-only (denies write tools on this connector)"
            />
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
              <Button onClick={cancel} disabled={submitting}>Cancel</Button>
              <Button variant="contained" onClick={approve} disabled={submitting || !token}>
                {submitting ? 'Connecting…' : 'Approve & connect'}
              </Button>
            </Stack>
          </>
        )}
      </Paper>
    </Box>
  );
}
